import { get } from 'lodash';
import { 
  PLAN_NAMES, 
  TERM_CONFIG, 
  SECTION_CONFIG,
  TRANSLATION_KEYS,
  SET_TOP_BOX_PRICING_RULES,
  getThemePackSectionKeys,
  getAddOnSectionKeys,
  getPromotionSectionKeys,
  getSetTopBoxSectionKeys,
  getInstallationSectionKeys,
  isAddOnAvailableForProfile
} from '../../vulcanHelpers/businessOptikTv/config';
import { businessOptikTvFieldNames } from '../../../utils/constant';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';

/**
 * Custom price calculator for Business Optik TV table
 * Calculates pricing based on table selections including:
 * - Term pricing (Month to Month vs 36 Months)
 * - Theme pack selections and pricing
 * - Add-ons pricing
 * - Promotions pricing
 * - Set top box pricing
 * - Installation pricing
 */
const getBusinessOptikTvPricing = (formFields, businessOptikTvTableData) => {
  
  const totals = {
    monthly: 0,
    oneTime: 0,
    threeYearEasyPayment: 0,
    fiveYearEasyPayment: 0
  };

  // Get selected rate plan and term
  const { monthlyRatePlanOptikTvVul, termBusinessOptikTvVul } = businessOptikTvFieldNames;
  const selectedRatePlan = get(formFields, `${monthlyRatePlanOptikTvVul}.value`);
  const selectedTerm = get(formFields, `${termBusinessOptikTvVul}.value`);

  // Get the table data directly from businessOptikTvTableData
  const { counters = {}, checkboxes = {}, dropdownSelections = {} } = businessOptikTvTableData || {};

  if (!selectedRatePlan || !selectedTerm) {
    return totals;
  }

  // Get the plan index for accessing price arrays (still needed for config arrays)
  const selectedPlanIndex = PLAN_NAMES.findIndex(plan => plan === selectedRatePlan);
  if (selectedPlanIndex === -1) {
    return totals;
  }

  // Helper function to get data with backward compatibility
  const getDataWithFallback = (dataObject, keyPrefix) => {
    // Try new format first (plan name)
    const newKey = `${keyPrefix}_${selectedRatePlan}`;
    if (dataObject && dataObject[newKey] !== undefined) {
      return dataObject[newKey];
    }
    
    // Fallback to old format (plan index) for backward compatibility
    if (selectedPlanIndex !== -1) {
      const oldKey = `${keyPrefix}_${selectedPlanIndex}`;
      return dataObject && dataObject[oldKey] !== undefined ? dataObject[oldKey] : undefined;
    }
    
    return undefined;
  };

  // Helper function to determine Set Top Box pricing rule
  const getSetTopBoxPricingRule = () => {
    const ruleEntries = Object.entries(SET_TOP_BOX_PRICING_RULES);
    const matchingRule = ruleEntries.find(
      ([, rule]) => rule.plans && rule.plans.includes(selectedRatePlan)
    );
    if (matchingRule) {
      return matchingRule[1];
    }
    
    // Default rule
    return SET_TOP_BOX_PRICING_RULES.DEFAULT;
  };

  // 1. PLAN PRICING - Get the complete plan price from TERM_CONFIG
  const termData = TERM_CONFIG[selectedTerm];
  if (termData && termData.prices[selectedPlanIndex] !== '-') {
    const planPrice = parseFloat(termData.prices[selectedPlanIndex].replace('$', ''));
    // Since term checkbox is now checked and disabled by default, always include plan pricing
    totals.monthly += planPrice;
  }

  // 3. THEME PACKS PRICING (excluding "Included Theme Packs" as they're free)
  const themePackSections = getThemePackSectionKeys().filter(section => 
    section !== SECTION_CONFIG.THEME_PACKS.INCLUDED.key
  );

  themePackSections.forEach(section => {
    const selections = getDataWithFallback(dropdownSelections, section);
    
    if (selections && Array.isArray(selections)) {
      selections.forEach(selection => {
        let themePack = null;
        
        // Find the theme pack in the correct array based on section config
        const sectionConfig = Object.values(SECTION_CONFIG.THEME_PACKS).find(config => config.key === section);
        if (sectionConfig) {
          themePack = sectionConfig.options.find(pack => pack.name === selection);
        }
        
        if (themePack && themePack.price) {
          const price = parseFloat(themePack.price.replace('$', ''));
          totals.monthly += price;
        }
      });
    }
  });

  // 4. ADD-ONS PRICING (respect add-on allowedProfileTypes from config)
  const { profileTypeOptikTvVul } = businessOptikTvFieldNames;
  const selectedProfileType = get(formFields, `${profileTypeOptikTvVul}.value`);
  const locale = getLocale();
  const translations = locale === 'en-US' ? EN : FR;
  const profileTypeTranslations = get(translations, 'vulcanWebform.businessOptikTv', {});

  getAddOnSectionKeys().forEach(sectionKey => {
    if (!isAddOnAvailableForProfile(sectionKey, selectedProfileType, profileTypeTranslations)) return;
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.ADD_ONS).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        if (price !== '-' && price !== 'Included') {
          const priceValue = parseFloat(price.replace('$', ''));
          totals.monthly += priceValue;
        }
      }
    }
  });

  // 5. PROMOTIONS PRICING (negative values for discounts)
  getPromotionSectionKeys().forEach(sectionKey => {
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.PROMOTIONS).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        if (price !== '-') {
          const priceValue = parseFloat(price.replace('$', ''));
          totals.monthly += priceValue; // This will be negative for discounts
        }
      }
    }
  });

  // 6. SET TOP BOX PRICING
  const pricingRule = getSetTopBoxPricingRule();
  
  getSetTopBoxSectionKeys().forEach(sectionKey => {
    const sectionConfig = Object.values(SECTION_CONFIG.SET_TOP_BOX).find(config => config.key === sectionKey);
    if (sectionConfig) {
      const count = getDataWithFallback(counters, sectionKey) || sectionConfig.defaultValue;
      
      if (count > 0) {
        // Calculate pricing based on the pricing rule
        let totalPrice = 0;
        
        if (sectionKey === 'Wired') {
          // First wired STB price is plan-dependent; on Month to Month, Core/Choices/More Choices charge $10 for the mandatory first box
          const isMtm = selectedTerm === 'Month to Month';
          let firstWiredPrice = pricingRule.firstWiredStbPrice !== undefined ? pricingRule.firstWiredStbPrice : 0;
          if (isMtm && pricingRule.mtmFirstWiredStbPrice !== undefined) {
            firstWiredPrice = pricingRule.mtmFirstWiredStbPrice;
          }
          totalPrice = firstWiredPrice;
          if (count > 1) {
            totalPrice += pricingRule.additionalStbPrice * (count - 1);
          }
        } else if (count === 1) {
          // For wireless STBs: use the original logic
          totalPrice = pricingRule.firstStbPrice;
        } else {
          totalPrice = pricingRule.firstStbPrice + (pricingRule.additionalStbPrice * (count - 1));
        }
        
        totals.monthly += totalPrice;
      }
    }
  });

  // 7. INSTALLATION PRICING
  getInstallationSectionKeys().forEach(sectionKey => {
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.INSTALLATION).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        if (price !== '-') {
          const priceValue = parseFloat(price.replace('$', ''));
          totals.oneTime += priceValue;
        }
      }
    }
  });

  return totals;
};

/**
 * Get price selections for Business Optik TV table
 * This is used for summary generation
 */
export const getBusinessOptikTvPriceSelections = (formFields, businessOptikTvTableData) => {
  const { monthlyRatePlanOptikTvVul } = businessOptikTvFieldNames;
  const selectedRatePlan = get(formFields, `${monthlyRatePlanOptikTvVul}.value`);
  
  if (!selectedRatePlan || !businessOptikTvTableData) {
    return {};
  }

  // Find the field name for the Business Optik TV table
  // Look for the field with type 'OptikTvTable' in formFields
  const tableFieldName = Object.keys(formFields).find(key => 
    formFields[key] && formFields[key].type === 'OptikTvTable'
  );
  
  if (!tableFieldName) {
    return {};
  }
  
  return {
    [tableFieldName]: getBusinessOptikTvPricing(formFields, businessOptikTvTableData)
  };
};

/**
 * Get totals for Business Optik TV pricing
 */
export const getBusinessOptikTvTotals = (formFields, businessOptikTvTableData) => {
  const { monthlyRatePlanOptikTvVul } = businessOptikTvFieldNames;
  const selectedRatePlan = get(formFields, `${monthlyRatePlanOptikTvVul}.value`);
  
  if (!selectedRatePlan || !businessOptikTvTableData) {
    return {
      monthly: 0,
      oneTime: 0,
      threeYearEasyPayment: 0,
      fiveYearEasyPayment: 0
    };
  }
  
  return getBusinessOptikTvPricing(formFields, businessOptikTvTableData);
};

/**
 * Generate summary details for Business Optik TV selections
 * This is used for quote contracts and summary display
 */
export const getBusinessOptikTvSummaryDetails = (formFields, businessOptikTvTableData) => {
  const { monthlyRatePlanOptikTvVul, termBusinessOptikTvVul, profileTypeOptikTvVul } = businessOptikTvFieldNames;
  const selectedRatePlan = get(formFields, `${monthlyRatePlanOptikTvVul}.value`);
  const selectedTerm = get(formFields, `${termBusinessOptikTvVul}.value`);
  const selectedProfileType = get(formFields, `${profileTypeOptikTvVul}.value`);

  if (!selectedRatePlan || !selectedTerm || !businessOptikTvTableData) {
    return [];
  }
  
  const { counters = {}, checkboxes = {}, dropdownSelections = {} } = businessOptikTvTableData;

  const summaryDetails = [];
  const selectedPlanIndex = PLAN_NAMES.findIndex(plan => plan === selectedRatePlan);

  if (selectedPlanIndex === -1) {
    return summaryDetails;
  }

  // Helper function to get data with backward compatibility
  const getDataWithFallback = (dataObject, keyPrefix) => {
    // Try new format first (plan name)
    const newKey = `${keyPrefix}_${selectedRatePlan}`;
    if (dataObject && dataObject[newKey] !== undefined) {
      return dataObject[newKey];
    }
    
    // Fallback to old format (plan index) for backward compatibility
    if (selectedPlanIndex !== -1) {
      const oldKey = `${keyPrefix}_${selectedPlanIndex}`;
      return dataObject && dataObject[oldKey] !== undefined ? dataObject[oldKey] : undefined;
    }
    
    return undefined;
  };

  // Helper function to determine Set Top Box pricing rule
  const getSetTopBoxPricingRule = () => {
    const ruleEntries = Object.entries(SET_TOP_BOX_PRICING_RULES);
    const matchingRule = ruleEntries.find(
      ([, rule]) => rule.plans && rule.plans.includes(selectedRatePlan)
    );
    if (matchingRule) {
      return matchingRule[1];
    }
    
    // Default rule
    return SET_TOP_BOX_PRICING_RULES.DEFAULT;
  };

  // 1. PLAN PRICING - Get the complete plan price from TERM_CONFIG
  const termData = TERM_CONFIG[selectedTerm];
  let totalPlanPrice = 0;
  
  if (termData && termData.prices[selectedPlanIndex] !== '-') {
    // Since term checkbox is now checked and disabled by default, always include plan pricing
    totalPlanPrice = parseFloat(termData.prices[selectedPlanIndex].replace('$', ''));
  }
  
  // Always add term entry first (no pricing, just label and description)
  summaryDetails.push({
    label: TRANSLATION_KEYS.TERM,
    description: selectedTerm
    // monthlyCharges: 0,
    // oneTimeCharges: 0,
    // threeYearEasyPaymentCharges: 0,
    // fiveYearEasyPaymentCharges: 0
  });

  // Profile Type under Term (no pricing, just label and description)
  if (selectedProfileType) {
    summaryDetails.push({
      label: TRANSLATION_KEYS.PROFILE_TYPE,
      description: selectedProfileType
    });
  }

  // Always add plan entry, even if pricing is $0 (when TERM_CONFIG shows "-")
  summaryDetails.push({
    label: TRANSLATION_KEYS.PLAN,
    description: selectedRatePlan.includes('(BTL)')
      ? selectedRatePlan.replace('(BTL)', '')
      : selectedRatePlan,
    qty: 1,
    monthlyCharges: totalPlanPrice,
    oneTimeCharges: 0,
    threeYearEasyPaymentCharges: 0,
    fiveYearEasyPaymentCharges: 0
  });

  // 3. THEME PACKS SELECTIONS
  const themePackSections = getThemePackSectionKeys();

  themePackSections.forEach(section => {
    const selections = getDataWithFallback(dropdownSelections, section);
    const rowData = {
      label: section.includes('(Additional Fee)')
        ? section.replace('(Additional Fee)', '')
        : section,
      monthlyCharges: 0,
      rows: []
    };

    if (selections && Array.isArray(selections) && selections.length > 0) {
      // For "Included Theme Packs", don't calculate price (they're free)
      const isIncluded = section === 'Included Theme Packs';
      // For each selection, create a separate row
      selections.forEach((selection, index) => {
        let price = 0;
        if (!isIncluded) {
          let themePack = null;
          
          // Find the theme pack in the correct array based on section config
          const sectionConfig = Object.values(SECTION_CONFIG.THEME_PACKS).find(config => config.key === section);
          if (sectionConfig) {
            themePack = sectionConfig.options.find(pack => pack.name === selection);
          }
          
          if (themePack && themePack.price) {
            price = parseFloat(themePack.price.replace('$', ''));
          }
        }
        
        // First item gets both label and description, subsequent items only get description
        const row = {
          qty: 1,
          monthlyCharges: price,
          description: selection,
          oneTimeCharges: 0,
          threeYearEasyPaymentCharges: 0,
          fiveYearEasyPaymentCharges: 0
        };

        rowData.monthlyCharges += price || 0;
        rowData.rows.push(row);
      });
       summaryDetails.push(rowData);
    }
  });

  // 4. ADD-ONS (respect add-on allowedProfileTypes from config)
  const localeSummary = getLocale();
  const translationsSummary = localeSummary === 'en-US' ? EN : FR;
  const profileTypeTranslationsSummary = get(translationsSummary, 'vulcanWebform.businessOptikTv', {});

  getAddOnSectionKeys().forEach(sectionKey => {
    if (!isAddOnAvailableForProfile(sectionKey, selectedProfileType, profileTypeTranslationsSummary)) return;
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.ADD_ONS).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        const priceValue = price === 'Included' ? 0 : parseFloat(price.replace('$', ''));
        summaryDetails.push({
          label: 'Add Ons',
          description: sectionKey,
          qty: 1,
          monthlyCharges: priceValue,
          oneTimeCharges: 0,
          threeYearEasyPaymentCharges: 0,
          fiveYearEasyPaymentCharges: 0
        });
      }
    }
  });

  // 5. PROMOTIONS
  getPromotionSectionKeys().forEach(sectionKey => {
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.PROMOTIONS).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        const priceValue = parseFloat(price.replace('$', ''));
        summaryDetails.push({
          label: 'Promotions',
          description: sectionKey,
          qty: 1,
          monthlyCharges: priceValue,
          oneTimeCharges: 0,
          threeYearEasyPaymentCharges: 0,
          fiveYearEasyPaymentCharges: 0
        });
      }
    }
  });

  // 6. SET TOP BOXES
  const setTopBoxItems = [];
  const pricingRule = getSetTopBoxPricingRule();
  
  getSetTopBoxSectionKeys().forEach(sectionKey => {
    const sectionConfig = Object.values(SECTION_CONFIG.SET_TOP_BOX).find(config => config.key === sectionKey);
    if (sectionConfig) {
      const count = getDataWithFallback(counters, sectionKey) || sectionConfig.defaultValue;
      
      if (count > 0) {
        // Calculate pricing based on the pricing rule
        let totalPrice = 0;
        
        if (sectionKey === 'Wired') {
          // First wired STB price is plan-dependent; on Month to Month, Core/Choices/More Choices charge $10 for the mandatory first box
          const isMtm = selectedTerm === 'Month to Month';
          let firstWiredPrice = pricingRule.firstWiredStbPrice !== undefined ? pricingRule.firstWiredStbPrice : 0;
          if (isMtm && pricingRule.mtmFirstWiredStbPrice !== undefined) {
            firstWiredPrice = pricingRule.mtmFirstWiredStbPrice;
          }
          totalPrice = firstWiredPrice;
          if (count > 1) {
            totalPrice += pricingRule.additionalStbPrice * (count - 1);
          }
        } else if (count === 1) {
          // For wireless STBs: use the original logic
          totalPrice = pricingRule.firstStbPrice;
        } else {
          totalPrice = pricingRule.firstStbPrice + (pricingRule.additionalStbPrice * (count - 1));
        }
        
        setTopBoxItems.push({
          rowType: sectionKey,
          count,
          totalPrice,
          price: pricingRule.additionalStbPrice // For display purposes, show the additional STB price
        });
      }
    }
  });

  // Add Set Top Box items with proper label/description structure
  const rowData = { label: 'Set Top Box', monthlyCharges: 0, rows: [] };
  setTopBoxItems.forEach((item, index) => {
    const row = {
      qty: item.count,
      monthlyCharges: item.totalPrice,
      description: item.rowType,
      oneTimeCharges: 0,
      threeYearEasyPaymentCharges: 0,
      fiveYearEasyPaymentCharges: 0,
      price: item.price // This is the price for additional STBs, used for display in summary
    };
    rowData.monthlyCharges += item.totalPrice || 0;
    rowData.rows.push(row);
  });
  summaryDetails.push(rowData);

  // 7. INSTALLATION
  const installationItems = [];
  getInstallationSectionKeys().forEach(sectionKey => {
    if (getDataWithFallback(checkboxes, sectionKey)) {
      const sectionConfig = Object.values(SECTION_CONFIG.INSTALLATION).find(config => config.key === sectionKey);
      if (sectionConfig) {
        const price = sectionConfig.prices[selectedPlanIndex];
        const priceValue = parseFloat(price.replace('$', ''));
        installationItems.push({
          rowType: sectionKey,
          priceValue
        });
      }
    }
  });
  
  // Add Installation items with proper label/description structure
  installationItems.forEach((item, index) => {
    const rowData = {
      qty: 1,
      monthlyCharges: 0,
      oneTimeCharges: item.priceValue,
      threeYearEasyPaymentCharges: 0,
      fiveYearEasyPaymentCharges: 0
    };
    
    if (index === 0) {
      // First item: both label and description
      rowData.label = "Installation";
      rowData.description = item.rowType;
    } else {
      // Subsequent items: only description
      rowData.description = item.rowType;
    }
    
    summaryDetails.push(rowData);
  });

  return summaryDetails;
};

/**
 * Build flat order summary rows for Optik TV (theme packs by type, add ons, promotions, set top boxes, installation).
 * Term and rate plan are omitted (shown elsewhere). Theme packs are separated by type.
 * Only includes selected items. Used by vulcanOrderSummary for FormSummaryRow display.
 */
export const getBusinessOptikTvOrderSummaryRows = (optikTvTableData, dataOrder) => {
  if (!optikTvTableData || !dataOrder) return [];

  const { monthlyRatePlanOptikTvVul } = businessOptikTvFieldNames;
  const ratePlanFromOrder = get(dataOrder, `${monthlyRatePlanOptikTvVul}.value`);
  const selectedRatePlan = ratePlanFromOrder != null ? ratePlanFromOrder : get(dataOrder, monthlyRatePlanOptikTvVul);

  if (!selectedRatePlan) return [];

  const { counters = {}, checkboxes = {}, dropdownSelections = {} } = optikTvTableData;
  const rows = [];

  const getVal = (dataObject, sectionKey) => {
    const key = `${sectionKey}_${selectedRatePlan}`;
    return dataObject && dataObject[key];
  };

  // Theme packs: one row per section type that has selections
  const themePackSections = getThemePackSectionKeys();
  themePackSections.forEach(sectionKey => {
    const selections = getVal(dropdownSelections, sectionKey);
    if (selections && Array.isArray(selections) && selections.length > 0) {
      rows.push({ label: sectionKey, value: selections.join(', ') });
    }
  });

  getAddOnSectionKeys().forEach(sectionKey => {
    if (getVal(checkboxes, sectionKey)) {
      rows.push({ label: 'Add ons', value: sectionKey });
    }
  });

  getPromotionSectionKeys().forEach(sectionKey => {
    if (getVal(checkboxes, sectionKey)) {
      rows.push({ label: 'Promotions', value: sectionKey });
    }
  });

  const wiredCount = getVal(counters, 'Wired');
  const wirelessCount = getVal(counters, 'Wireless');
  const wiredNum = wiredCount !== undefined && wiredCount !== null ? Number(wiredCount) : 1;
  const wirelessNum = wirelessCount !== undefined && wirelessCount !== null ? Number(wirelessCount) : 0;
  rows.push({
    label: 'Set Top Box',
    value: `Wired: ${wiredNum}, Wireless: ${wirelessNum}`
  });

  const installationSelected = [];
  getInstallationSectionKeys().forEach(sectionKey => {
    if (getVal(checkboxes, sectionKey)) {
      installationSelected.push(sectionKey);
    }
  });
  if (installationSelected.length > 0) {
    rows.push({ label: 'Installation type', value: installationSelected.join(', ') });
  }

  return rows;
};

export default getBusinessOptikTvPricing;
